<template>
  <v-card class="ma-2" style="flex: 1 0 auto" :class="{ disable: loadingFtp }">
    <v-card-title
      class="pb-0"
      style="flex: 1 0 auto; flex-direction: column; align-items: stretch; position: relative; height: 100%"
    >
      <v-layout class="pb-0" row wrap style="flex: 0 0; align-items: flex-start; border-bottom: 1px solid #ccc">
        <span class="subheading mb-1" style="flex: 0 0 auto">{{ $t('Pilot.FtpRsync.ConfigurationFtp') }}</span>
        <v-spacer />
        <v-switch
          hide-details
          class="pb-2 sm-switch"
          v-model="configFtp.enableFtp"
          @change="saveConfigFtp"
          :label="$t('Pilot.FtpRsync.EnableFtp')"
          style="flex: 0 0 auto"
        />
      </v-layout>
      <div style="flex: 1 0; position: relative">
        <v-layout
          class="pb-0"
          row
          wrap
          style="flex: 1 0; align-items: flex-start; overflow: auto; position: absolute; height: 100%; width: 100%"
        >
          <v-flex xs6 pr-2 class="inline-block">
            <v-text-field hide-details v-model="configFtp.server" :label="$t('Pilot.FtpRsync.Server')" />
            <number-field-factor hide-details v-model.number="configFtp.port" :label="$t('Pilot.FtpRsync.Port')" />
            <v-text-field hide-details v-model="configFtp.login" :label="$t('Pilot.FtpRsync.Login')" />
            <v-text-field hide-details v-model="configFtp.password" :label="$t('Pilot.FtpRsync.Password')" />
            <v-switch
              class="px-0 pb-2 sm-switch"
              hide-details
              v-model="configFtp.tls"
              :label="$t('Pilot.FtpRsync.Tls')"
            />
          </v-flex>
          <v-flex xs6 pl-2 class="inline-block">
            <div class="mt-2">{{ $t('Pilot.FtpRsync.SyncPaths') }} :</div>
            <div class="checkboxGroup">
              <v-checkbox
                hide-details
                v-for="syncPath in SyncPathsArr"
                :key="syncPath"
                class="ml-0 my-0"
                v-model="configFtp.syncPaths"
                :title="$t(`Pilot.FtpRsync.${syncPath}Tooltip`)"
                :label="syncPath"
                :value="syncPath"
                style="flex: 0 0 !important; min-width: 51px; min-height: 20px"
              >
              </v-checkbox>
            </div>
            <v-text-field hide-details v-model="configFtp.dstPaths" :label="$t('Pilot.FtpRsync.DstPaths')" />
          </v-flex>
        </v-layout>
      </div>
      <v-layout class="pt-0" row wrap style="flex: 0 0; align-items: flex-start">
        <v-spacer />
        <v-btn @click.prevent="restoreConfigFtp" :disabled="!btnActifFtp" small
          >{{ $t('Pilot.FtpRsync.RestoreConfig') }}<v-icon small right>mdi-replay</v-icon></v-btn
        >
        <v-btn @click.prevent="saveConfigFtp" :disabled="!btnActifFtp" small color="primary"
          >{{ $t('Pilot.FtpRsync.ValidateConfig') }}<v-icon small right>mdi-content-save</v-icon></v-btn
        >
      </v-layout>
    </v-card-title>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import deepEqual from 'deep-equal'
import { IrisConfigContainer } from '@/models/iris/IrisConfigContainer'
import { IFtpConfig } from '@/models/iris/ftp/IFtpConfig'
import { SyncPathsArr } from '@/enum/SyncPath'
import { AloMission } from '@/models/alo/AloMission'
import store from '@/store'

// Props
interface Props {
  mission: AloMission
}

const props = defineProps<Props>()

// Reactive data
const configFtp = ref({
  enableFtp: true,
  login: '',
  password: '',
  port: 21,
  server: '',
  tls: true,
  dstPaths: '',
  syncPaths: [] as string[]
})

// Computed properties

const originalFtpConfig = computed(() => {
  if (props.mission.gliId == null) return null
  return store.myGetters.originalFtpConfigs[props.mission.gliId]
})

const modifiedFtpConfig = computed(() => {
  if (props.mission.gliId == null) return null
  return store.myGetters.modifiedFtpConfigs[props.mission.gliId]
})

const loadingFtp = computed(() => store.myGetters.ftpLoading)

const btnActifFtp = computed(() => {
  if (!originalFtpConfig.value) return false
  const configFtpCopy = loadConfigFtp({}, originalFtpConfig.value)
  return !deepEqual(configFtpCopy, configFtp.value) && !loadingFtp.value
})

// Methods
function loadConfigFtp(target: any, config: IrisConfigContainer<IFtpConfig>) {
  if (config) {
    target.enableFtp = config.mod_settings.EnabledMOD['module-FTP']
    target.login = config.mod_settings.FTP.login
    target.password = config.mod_settings.FTP.password
    target.port = Number.parseInt(config.mod_settings?.FTP?.port?.toString()) || 21
    target.server = config.mod_settings.FTP.server
    target.tls = config.mod_settings.FTP.tls
    target.dstPaths = config.mod_settings.Data.dst_paths
    target.syncPaths = Array.isArray(config.mod_settings.Data.sync_paths) ? config.mod_settings.Data.sync_paths : []
  }
  return target
}

function validateConfigFtp() {
  if (props.mission.gliId == null) return

  store.myActions.GLIDER_UPDATE_MODIFIED_FTP({
    itemId: props.mission.gliId,
    newProperties: {
      mod_settings: {
        EnabledMOD: {
          'module-FTP': configFtp.value.enableFtp
        },
        FTP: {
          login: configFtp.value.login,
          password: configFtp.value.password,
          port: configFtp.value.port,
          server: configFtp.value.server,
          tls: configFtp.value.tls
        },
        Data: {
          dst_paths: configFtp.value.dstPaths,
          sync_paths: configFtp.value.syncPaths.filter((p) => SyncPathsArr.some((pa) => pa == p))
        }
      }
    }
  })
}

function saveConfigFtp() {
  validateConfigFtp()
  if (props.mission.gliId != null) {
    store.myActions.GLIDER_IRIS_FTP_SAVE_REQUEST(props.mission.gliId)
  }
}

function restoreConfigFtp() {
  if (props.mission.gliId != null) {
    store.myActions.GLIDER_CANCEL_MODIFIED_FTP(props.mission.gliId)
  }
}

// Watchers
watch(
  () => props.mission,
  (newValue) => {
    if (newValue.gliId != null) {
      store.myActions.GLIDER_IRIS_SINGLE_WAY_POINT_REQUEST(newValue.gliId)
    }
  }
)

watch(
  modifiedFtpConfig,
  (newConfig: IrisConfigContainer<IFtpConfig> | null, oldConfig: IrisConfigContainer<IFtpConfig> | null) => {
    if (newConfig && !deepEqual(newConfig, oldConfig)) {
      configFtp.value = loadConfigFtp({}, newConfig)
    }
  },
  { deep: true }
)

watch(
  configFtp,
  () => {
    validateConfigFtp()
  },
  { deep: true }
)

// Created lifecycle
if (props.mission.gliId != null) {
  store.myActions.GLIDER_IRIS_FTP_REQUEST(props.mission.gliId).then(() => {
    if (modifiedFtpConfig.value) {
      configFtp.value = loadConfigFtp({}, modifiedFtpConfig.value)
    }
  })
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scope>
.subheading {
  font-weight: bold;
}

.checkboxGroup {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  position: relative;
}
</style>

<style>
.checkboxGroup .v-input__control {
  transform: scale(0.63);
  transform-origin: 0 0;
  position: absolute;
  min-width: fit-content;
  font-weight: bold;
  white-space: nowrap;
}

.radioGroup .v-input--radio-group__input {
  flex-direction: row;
  min-width: fit-content;
  display: flex;
  position: relative;
  flex-wrap: wrap;
}
</style>
